/**
 * ============================================================================
 * CONFLICT WARNING COMPONENT v1.0
 * ============================================================================
 * Zeigt eine Warnung bei überlappenden Terminen an
 *
 * Features:
 * - Zeigt konfligierende Termine an
 * - Optionen: Trotzdem erstellen oder Abbrechen
 * - Responsive Design
 * - Dark Mode Support
 *
 * @version 1.0.0
 * ============================================================================
 */

import React from 'react';
import { useTheme } from '../../context/ThemeContext';

function ConflictWarning({ conflicts, onProceed, onCancel, isLoading = false }) {
  const { isDark } = useTheme();

  const colors = isDark ? {
    bg: '#1F2937',
    bgWarning: '#451A03',
    text: '#F9FAFB',
    textMuted: '#9CA3AF',
    border: '#D97706',
    borderLight: '#92400E',
    warningText: '#FCD34D',
    warningBg: '#78350F'
  } : {
    bg: '#FFFFFF',
    bgWarning: '#FFFBEB',
    text: '#1F2937',
    textMuted: '#6B7280',
    border: '#F59E0B',
    borderLight: '#FDE68A',
    warningText: '#B45309',
    warningBg: '#FEF3C7'
  };

  const formatTime = (time) => {
    if (!time) return '';
    return time.slice(0, 5);
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit'
      });
    } catch {
      return date;
    }
  };

  if (!conflicts || conflicts.length === 0) return null;

  return (
    <div style={{
      background: colors.bgWarning,
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '1rem',
      marginBottom: '1rem'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '0.75rem'
      }}>
        <span style={{ fontSize: '1.5rem' }}>&#x26A0;&#xFE0F;</span>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          fontWeight: 700,
          color: colors.warningText
        }}>
          Terminkonflikt erkannt!
        </h3>
      </div>

      {/* Description */}
      <p style={{
        margin: '0 0 0.75rem 0',
        fontSize: '0.9rem',
        color: colors.text
      }}>
        Der neue Termin überschneidet sich mit {conflicts.length === 1 ? 'einem bestehenden Termin' : `${conflicts.length} bestehenden Terminen`}:
      </p>

      {/* Conflict List */}
      <div style={{
        background: colors.warningBg,
        borderRadius: '8px',
        padding: '0.5rem',
        marginBottom: '1rem',
        maxHeight: '150px',
        overflow: 'auto'
      }}>
        {conflicts.map((conflict, idx) => (
          <div key={conflict.id || idx} style={{
            padding: '8px 10px',
            borderRadius: '6px',
            marginBottom: idx < conflicts.length - 1 ? '6px' : 0,
            background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)',
            border: `1px solid ${colors.borderLight}`
          }}>
            <div style={{
              fontWeight: 600,
              fontSize: '0.9rem',
              color: colors.text,
              marginBottom: '4px'
            }}>
              {conflict.title}
            </div>
            <div style={{
              fontSize: '0.8rem',
              color: colors.textMuted,
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>&#x1F4C5;</span> {formatDate(conflict.date)}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>&#x1F551;</span> {formatTime(conflict.time)}
              </span>
              {conflict.category && (
                <span style={{
                  background: 'rgba(74, 144, 164, 0.2)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '0.75rem'
                }}>
                  {conflict.category}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={onCancel}
          disabled={isLoading}
          style={{
            padding: '8px 16px',
            background: isDark ? '#374151' : '#F3F4F6',
            color: colors.textMuted,
            border: `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Abbrechen
        </button>
        <button
          onClick={onProceed}
          disabled={isLoading}
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.2s'
          }}
        >
          {isLoading ? (
            <>
              <span style={{
                width: '14px',
                height: '14px',
                border: '2px solid white',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
              Speichere...
            </>
          ) : (
            <>
              &#x26A0;&#xFE0F; Trotzdem erstellen
            </>
          )}
        </button>
      </div>

      {/* Spin Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ConflictWarning;
